/**
 * Copyright © 2022-2023 Delicious AI, LLC
 *
 */

const PRICING_UPC = '000000000017'
const OUT_OF_STOCK_UPC = '000000000018'
const isPriceTag = (upc: string) => upc === PRICING_UPC
const isOutOfStockTag = (upc: string) => upc === OUT_OF_STOCK_UPC

// Convert UPC to 12-digit format with proper check digit calculation
const convertTo12DigitUPC = (upc: string): string | null => {
  // Remove any non-numeric characters
  const cleanUpc = upc.replace(/\D/g, '')
  if (cleanUpc.length < 11) {
    return null
  }

  // If it's already 12 digits, validate the check digit
  if (cleanUpc.length === 12) {
    const calculatedCheckDigit = calculateUPCCheckDigit(
      cleanUpc.substring(0, 11),
    )
    const providedCheckDigit = parseInt(cleanUpc[11])

    // If check digit is correct, return as-is
    if (calculatedCheckDigit === providedCheckDigit) {
      return cleanUpc
    }
    // If check digit is wrong, recalculate it
    return cleanUpc.substring(0, 11) + calculatedCheckDigit
  }

  if (cleanUpc.length === 11) {
    const checkDigit = calculateUPCCheckDigit(cleanUpc)
    return cleanUpc + checkDigit
  }

  return null
}

// Calculate UPC-A check digit using the standard algorithm
const calculateUPCCheckDigit = (first11Digits: string): number => {
  if (first11Digits.length !== 11) {
    throw new Error('UPC must be 11 digits for check digit calculation')
  }

  let sum = 0

  // Add digits in odd positions (1st, 3rd, 5th, 7th, 9th, 11th) multiplied by 3
  for (let i = 0; i < 11; i += 2) {
    sum += parseInt(first11Digits[i]) * 3
  }

  // Add digits in even positions (2nd, 4th, 6th, 8th, 10th)
  for (let i = 1; i < 11; i += 2) {
    sum += parseInt(first11Digits[i])
  }

  // Check digit is the amount needed to round up to next multiple of 10
  const checkDigit = (10 - (sum % 10)) % 10

  return checkDigit
}

export const CommonProductHelpers = {
  isPriceTag,
  isOutOfStockTag,
  convertTo12DigitUPC,
}
